import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { createSelector } from '@reduxjs/toolkit';
import pick from 'lodash/fp/pick';
import { useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import useTableViews from 'dashboard/lib/hooks/useTableViews';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ApiLogAttributes } from 'dashboard/models/api-log';
import { TableKey } from 'dashboard/models/table-view';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  ApiLogResponseStatus,
  setCurrentPage,
  setCurrentTableView,
  setFilters,
  setPageSize,
  setTableViews,
} from 'dashboard/redux/slices/api-logs';
import { RootState } from 'dashboard/redux/store';
import { loadApiKeyList } from 'dashboard/redux/thunk-actions/api-keys';
import { loadApiLogList } from 'dashboard/redux/thunk-actions/api-logs';
import DashboardApiLogsView from 'dashboard/views/DashboardApiLogsView';
import useApiLoader from 'lib/hooks/useApiLoader';
import usePermission from 'lib/hooks/usePermission';
import useTable from 'lib/hooks/useTable';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

type DashboardApiLogsRouteProps = PropsFromRedux;

const getTableViewState = (state: RootState) => state.apiLogs.tableViews;
const selectTableViews = createSelector(getTableViewState, (state) => state.views);
const selectCurrentTableView = createSelector([getTableViewState], (tableViews) => {
  return tableViews.views.find((tv) => tv.id === tableViews.currentTableView);
});

const DashboardApiLogsRoute = (props: DashboardApiLogsRouteProps) => {
  const { currentEnvironment, currentPage, filters, pageSize, setTableViews, setCurrentTableView } =
    props;

  const { handlePageLinkClick, handlePageSizeClick, handleDateUpdate, handleOmniSearch } = useTable(
    {
      filters: filters,
      pageSize: pageSize,
      currentPage: currentPage,
      setFilters: props.setFilters,
      setCurrentPage: props.setCurrentPage,
      setPageSize: props.setPageSize,
    },
  );

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        if (startDate || endDate) {
          handleDateUpdate({ startDate, endDate }, { time: true });
        } else {
          handleDateUpdate({ startDate: undefined, endDate: undefined });
        }
      }
    },
    [handleDateUpdate],
  );

  const handleStatusFilterUpdate = (statuses: ApiLogResponseStatus[]) => {
    props.setFilters({ status: statuses });
    handlePageLinkClick(1);
  };

  const handleMethodsFilterUpdate = (methods: ApiLogAttributes['request']['method'][]) => {
    props.setFilters({ methods });
    handlePageLinkClick(1);
  };

  const handleApiKeyFilterUpdate = (apiKeyIds: string[]) => {
    props.setFilters({ apiKeyIds: apiKeyIds });
    handlePageLinkClick(1);
  };

  const { loading } = useApiLoader({
    loader: () =>
      props.loadApiLogList({
        currentEnvironment,
        currentPage: currentPage ?? 1,
        pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
        filters: filters ?? {},
      }),
    deps: [props.loadApiLogList, currentEnvironment, currentPage, pageSize, filters],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load API logs.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const hasApiKeyViewPermission = usePermission(Permission.ApiKeyView);
  useApiLoader({
    loader: () => {
      if (hasApiKeyViewPermission) {
        return props.loadApiKeyList({ currentEnvironment });
      }

      return Promise.resolve();
    },
    deps: [props.loadApiKeyList, currentEnvironment],
    onError: () => props.showMessage('Failed to load API Keys.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const tableViewsState = useTableViews({
    tableKey: TableKey.ApiLogsIndex,
    filters: pick(['status', 'methods', 'apiKeyIds'], filters),
    onSetTableView: (tableView) => {
      if (!tableView) {
        props.setFilters({
          status: undefined,
          methods: undefined,
          apiKeyIds: undefined,
        });
        return;
      }

      const tableViewFilters = tableView.attributes.configuration.filters;
      props.setFilters({
        status: tableViewFilters.status,
        methods: tableViewFilters.methods,
        apiKeyIds: tableViewFilters.apiKeyIds,
      });
    },
    setCurrentTableView,
    setTableViews,
    selectTableViews,
    selectCurrentTableView,
  });

  return (
    <DashboardApiLogsView
      apiLogs={props.apiLogs}
      apiKeys={props.apiKeys}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      pageSize={pageSize ?? DEFAULT_PAGE_SIZE}
      isLoading={loading}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdateV2}
      handleOmniSearch={handleOmniSearch}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleMethodsFilterUpdate={handleMethodsFilterUpdate}
      handleApiKeyFilterUpdate={handleApiKeyFilterUpdate}
      tableViewsState={tableViewsState}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    apiLogs: state.apiLogs.apiLogs[state.currentEnvironment.currentEnvironment.id] || [],
    apiKeys: state.apiKeys.apiKeys,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    pageSize: state.apiLogs.pageSize,
    currentPage: state.apiLogs.currentPage,
    paginationParams: state.apiLogs.paginationParams,
    filters: state.apiLogs.filters,
  };
};

const mapDispatchToProps = {
  loadApiLogList,
  loadApiKeyList,
  setFilters,
  setPageSize,
  setCurrentPage,
  showMessage,
  setTableViews,
  setCurrentTableView,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardApiLogsRoute);
